import * as React from 'react';
import { Link } from 'react-router-dom';

import { formatDateTime } from '@/domains/survey';
import type {
  IFigureActor,
  IFigureAddress,
  IFigureCell,
  IFigureEvent,
  IFigureHistory
} from '../../api/figure-contracts';
import { figuresApi } from '../../api/figures-api';
import { absent } from '../../components/account-facts';
import { InternalFieldDisplay } from '../../components/field-controls';
import { withBatch } from '../../components/history-batch';
import { HistoryPopover, type IHistoryPage, type IHistoryRow } from '../../components/history-popover';
import { ORIGIN_WORDS, figureEventWords, wordFor } from '../../figures';
import { responsePath } from '../../paths';

/**
 * One figure's history under Asset & production (the advisor record page's F5, W6; its plan's R6):
 * survey-api's `…/figures/{survey}/{metric}/{scope}/{asOf}/events`, turned into the kit's
 * `HistoryPopover` rows, beside the cell's pencil — ten a page, page 1 read afresh on each opening.
 *
 * - **Above the rows, the cell as it is now** — the history's own `cell`, read with the page, never
 *   the table's: "Stored now: <value>" or "Cleared", **where it came from** in words — a migrated
 *   cell's Salesforce record as selectable text (no link: no Salesforce base URL is configured, and
 *   Salesforce is being switched off), an answer's response as a link to it in the Team Portal,
 *   or the Veritant source in words — and who set it, when. This is where the table's face sends
 *   what it does not draw (the roadmap's D11).
 * - **A row is one row of the metrics journal as sent** (SR-451), newest first, never sorted:
 *   When, Who, What (its source in words — "Answered", "Set", "Firm’s file", "Cleared" for a
 *   Veritant row with no value, any other as spelled), From (the row before it), To (an amount,
 *   "—" for none), and Reason, which the popover draws only where a row carries one.
 * - **Who**: an account by its email, "an account since removed" without one, the role as spelled
 *   for an event no account made — research-history's words.
 *
 * Rejected: drawing the origin on the table's face — a column of provenance beside every amount,
 * which survey-api sends for the history's sake.
 */

/** An amount as a history cell draws it, "—" for none. */
function amount(value: Nullable<number>): React.ReactNode {
  return value === null ? absent('—') : <InternalFieldDisplay payload={{ t: 'money', v: value }} />;
}

function who(actor: Nullable<IFigureActor>): React.ReactNode {
  if (actor === null) return absent('No account');
  if (actor.email) return actor.email;
  if (!actor.accountId) return absent(`No account (${actor.role})`);
  return absent('an account since removed');
}

/** Where a cell's value came from, in words — the origin's, then its source's own members. */
function provenance(cell: IFigureCell): React.ReactNode {
  const said = wordFor(ORIGIN_WORDS, cell.origin);
  const { source } = cell;
  if (source?.kind === 'salesforce') {
    const record = [source.object, source.field].filter(Boolean).join(' ');
    return (
      <span className="select-text">
        {said}
        {record && `: ${record}`}
        {source.recordId && `, record ${source.recordId}`}
      </span>
    );
  }
  if (source?.kind === 'response' && source.responseId) {
    return (
      <>
        {said}{' '}
        <Link className="underline" to={responsePath(source.responseId)}>
          Open the response
        </Link>
      </>
    );
  }
  return said;
}

/** The cell as it is now, above the rows. */
function StoredNow({ cell }: { cell: IFigureCell }) {
  return (
    <div className="grid gap-1" data-stored>
      <div className="flex flex-wrap items-baseline gap-x-1.5">
        {cell.value === null ? (
          <span>Cleared</span>
        ) : (
          <>
            <span className="text-muted-foreground">{'Stored now: '}</span>
            {amount(cell.value)}
          </>
        )}
      </div>
      <div>{provenance(cell)}</div>
      <div className="text-xs text-muted-foreground">
        {cell.actor ? (
          <>
            Set by {who(cell.actor)}, {formatDateTime(cell.setAt)}
          </>
        ) : (
          <>Set {formatDateTime(cell.setAt)}</>
        )}
      </div>
    </div>
  );
}

/** survey-api's history document as the popover's page (R6). */
function figureHistoryPage(history: IFigureHistory): IHistoryPage {
  const items = history.items.map(
    (event: IFigureEvent, index): IHistoryRow => ({
      // An event carries no id; its place in the page is unique within the page.
      key: `${history.page}:${index}`,
      at: event.at,
      who: who(event.actor),
      what: withBatch(figureEventWords(event.kind, event.new), event.batch),
      from: amount(event.old),
      to: amount(event.new),
      reason: event.reason
    })
  );
  return {
    items,
    page: history.page,
    pageSize: history.pageSize,
    total: history.total,
    above: history.cell === null ? undefined : <StoredNow cell={history.cell} />
  };
}

/** The history button of one cell about one member. */
export function FigureHistory({
  participantId,
  address,
  label,
  name
}: {
  participantId: string;
  address: IFigureAddress;
  /** The cell's name — "<label>, <scope>, as of <day>". */
  label: string;
  /** The member's name — "History of <label> for <name>". */
  name: string;
}) {
  return (
    <HistoryPopover
      label={label}
      name={name}
      emptyText="No change to this figure is recorded."
      read={async (page, signal) =>
        figureHistoryPage(await figuresApi.history(participantId, address, page, signal))
      }
    />
  );
}
